import { useEffect, useState } from "react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Switch } from "../../ui/switch";
import { X } from "lucide-react";
import {
  useUpdateEligibilityCondition,
  type ComparisonOperator,
  type EligibilityComparison,
  type EligibilityCondition,
} from "../../utils/portfolio-risk-concentration/api/eligibility";

const OPERATORS: ComparisonOperator[] = [">=", ">", "<=", "<", "=", "<>"];

/**
 * Adjust one raw investor code's eligibility condition — direct write, no propose/approve
 * (that table has none, see eligibility.py's module docstring).
 *
 * Adjust-only since sql/43_: every code always carries exactly 9 fixed conditions, seeded
 * automatically — there is no "+ Add Condition" any more, so `condition` is always given.
 * List conditions (Bankruptcy, Risk Rating, PG Flag, Payment Frequency, Asset Type, SIC,
 * Residual Type) edit a set of excluded values as removable chips; Comparison conditions
 * (FICO, Contract Term) edit a list of operator+value pairs, OR'd.
 */
export function EligibilityConditionDialog({
  open,
  onOpenChange,
  condition,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  condition: EligibilityCondition | null;
  onSubmitted?: (message: string) => void;
}) {
  const update = useUpdateEligibilityCondition();

  const [values, setValues] = useState<string[]>([]);
  const [newValue, setNewValue] = useState("");
  const [comparisons, setComparisons] = useState<EligibilityComparison[]>([]);
  const [status, setStatus] = useState<"ON" | "OFF">("OFF");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !condition) return;
    setError(null);
    setValues(condition.exclusionValues);
    setComparisons(condition.exclusionComparisons);
    setStatus(condition.status);
    setNewValue("");
  }, [open, condition]);

  function addValue() {
    const v = newValue.trim();
    if (!v || values.includes(v)) return;
    setValues((vs) => [...vs, v]);
    setNewValue("");
  }

  function addComparison() {
    setComparisons((cs) => [...cs, { operator: ">=", value: 0 }]);
  }

  async function submit() {
    if (!condition) return;
    setError(null);

    const isList = condition.conditionType === "List";
    if (status === "ON" && isList && values.length === 0) {
      setError("Turning this ON needs at least one value.");
      return;
    }
    if (status === "ON" && !isList && comparisons.length === 0) {
      setError("Turning this ON needs at least one comparison.");
      return;
    }
    for (const c of comparisons) {
      if (!Number.isFinite(c.value)) {
        setError("Every comparison needs a number.");
        return;
      }
    }

    try {
      await update.mutateAsync({
        eligibilityId: condition.eligibilityId,
        status,
        ...(isList ? { exclusionValues: values } : { exclusionComparisons: comparisons }),
      });
      onSubmitted?.(
        `${condition.condition} updated for ${condition.facilityLabel} (code ${condition.bankdwnInvestorCode}).`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Condition</DialogTitle>
          <DialogDescription>
            A condition that matches here takes a contract out of the pool entirely, for every
            valuation method — not "worth zero," simply not counted.
          </DialogDescription>
        </DialogHeader>

        {condition && (
          <div className="space-y-4">
            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}

            <div>
              <Label>Investor Code</Label>
              <p className="mt-1 text-sm text-muted-foreground">
                {condition.bankdwnInvestorCode} — {condition.facilityLabel} · {condition.condition}
              </p>
            </div>

            {condition.conditionType === "List" ? (
              <div>
                <Label>Excluded Values</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {values.length === 0 && (
                    <p className="text-xs text-muted-foreground">No values selected.</p>
                  )}
                  {values.map((v) => (
                    <Badge
                      key={v}
                      variant="outline"
                      className="gap-1 border-border px-1.5 py-0 font-mono text-[10px]"
                    >
                      {v}
                      <button
                        type="button"
                        className="hover:text-destructive"
                        onClick={() => setValues((vs) => vs.filter((x) => x !== v))}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    className="h-8"
                    placeholder='e.g. Y, or "NULL" for a missing value'
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addValue();
                      }
                    }}
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={addValue}>
                    Add
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Label>Comparisons (matches any)</Label>
                <div className="mt-1 space-y-2">
                  {comparisons.length === 0 && (
                    <p className="text-xs text-muted-foreground">No comparisons set.</p>
                  )}
                  {comparisons.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Select
                        value={c.operator}
                        onValueChange={(v) =>
                          setComparisons((cs) =>
                            cs.map((x, j) => (j === i ? { ...x, operator: v as ComparisonOperator } : x)),
                          )
                        }
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORS.map((op) => (
                            <SelectItem key={op} value={op}>
                              {op}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        className="h-8"
                        inputMode="decimal"
                        value={c.value}
                        onChange={(e) =>
                          setComparisons((cs) =>
                            cs.map((x, j) =>
                              j === i ? { ...x, value: Number(e.target.value) } : x,
                            ),
                          )
                        }
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => setComparisons((cs) => cs.filter((_, j) => j !== i))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-2 h-8" onClick={addComparison}>
                  + Add comparison
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="eligibility-status" className="font-normal">
                {status === "ON" ? "On" : "Off"}
              </Label>
              <Switch
                id="eligibility-status"
                checked={status === "ON"}
                onCheckedChange={(v) => setStatus(v ? "ON" : "OFF")}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={update.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={update.isPending || !condition} onClick={submit}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
